<template>
  <app-layout title="Detail Pengadaan Diajukan Menang">
    <template #header>
      <div class="flex items-center justify-between">
        <h2 class="font-semibold text-xl text-gray-800 leading-tight">
          Detail Pengadaan Diajukan Menang
        </h2>
        <button
          @click="goBack"
          class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-200 active:bg-gray-600 disabled:opacity-25 transition"
        >
          <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Kembali
        </button>
      </div>
    </template>

    <div class="py-12">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <!-- Document Information -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6 mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Informasi Dokumen Pengadaan</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Nama Paket</label>
              <p class="mt-1 text-sm text-gray-900">{{ dokumen.paket_pengadaan?.nama_paket_pengadaan || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Nomor BAHP</label>
              <p class="mt-1 text-sm text-gray-900">{{ dokumen.nomor_bahp || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Tanggal BAHP</label>
              <p class="mt-1 text-sm text-gray-900">{{ formatDate(dokumen.tanggal_bahp) || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Status</label>
              <p class="mt-1 text-sm text-gray-900">{{ dokumen.tahap?.nama_tahap || '-' }}</p>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="mt-6 flex space-x-4">
            <Button 
              @click="showHPSModal"
              icon="pi pi-calculator"
              label="Lihat HPS"
              severity="info"
              size="small"
              text 
            />
            <Button 
              v-if="dokumen.nomor_baep && dokumen.tanggal_baep"
              @click="showBaepModal"
              icon="pi pi-file-pdf"
              label="Lihat BAEP"
              severity="success"
              size="small"
              text 
            />
            <Button 
              v-if="dokumen.nomor_bahp && dokumen.tanggal_bahp"
              @click="showBahpModal"
              icon="pi pi-file-pdf"
              label="Lihat BAHP !"
              severity="success"
              size="small"
              text 
            />
            <Button 
              v-if="canSetujuiPemenang"
              @click="confirmSetujuiPemenang"
              icon="pi pi-check"
              label="Setujui Pemenang"
              severity="success"
              size="small"
              :loading="isSubmittingSetujui"
            />
            <Button 
              v-if="canTolakPemenang"
              @click="confirmTolakPemenang"
              icon="pi pi-times"
              label="Tolak Persetujuan Pemenang"
              severity="danger"
              size="small"
              :loading="isSubmittingTolak"
            />
          </div>
        </div>

        <!-- Pengajuan List -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Daftar Pengajuan</h3>
          
          <div v-if="pengajuan.length > 0">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Penyedia</th>
                    <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Nilai Penawaran</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lulus / Tidak</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keterangan</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(item, index) in pengajuan" :key="item.id">
                    <td class="px-6 py-4 whitespace-nowrap text-sm">{{ index + 1 }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-gray-900">
                        {{ item.penyedia?.nama_perusahaan || '-' }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {{ formatCurrency(getTotalNilaiPenawaran(item)) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div v-if="item.penilaian_penyedia_dokumen_pengadaan && item.penilaian_penyedia_dokumen_pengadaan.length > 0" class="text-sm">
                        <div v-for="(penilaian, pidx) in item.penilaian_penyedia_dokumen_pengadaan" :key="pidx" class="mb-1">
                          <span :class="penilaian.is_lulus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" 
                                class="px-2 py-1 text-xs font-medium rounded-full">
                            {{ penilaian.is_lulus ? 'Lulus' : 'Tidak Lulus' }}
                          </span>
                          <span class="text-xs text-gray-500 ml-1">
                            {{ penilaian.bobot_penilaian_dokumen_pengadaan ? penilaian.bobot_penilaian_dokumen_pengadaan.nama : '' }}
                          </span>
                        </div>
                      </div>
                      <div v-else class="text-sm text-gray-500">-</div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="text-sm text-gray-900">
                        {{ item.keterangan_penilaian || '-' }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span v-if="item.is_pemenang" class="inline-flex items-center text-green-600">
                        <i class="fas fa-trophy mr-1"></i>
                        Pemenang
                      </span>
                      <span v-else-if="item.is_proposed_pemenang" class="inline-flex items-center text-blue-600">
                        <i class="fas fa-user-check mr-1"></i>
                        Diusulkan Pemenang
                      </span>
                      <span v-else class="text-gray-500">-</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm">
                      <Button 
                        @click="showPenilaianDetail(item)"
                        icon="pi pi-eye"
                        label="Lihat Penilaian"
                        severity="info"
                        size="small"
                        text 
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="text-center py-4 text-gray-500">
            Tidak ada pengajuan yang ditemukan
          </div>
        </div>
      </div>
    </div>

    <!-- HPS Modal -->
    <dialog-modal :show="hpsModalOpen" @close="closeHPSModal" :max-width="'7xl'">
      <template #title>
        HPS (Harga Perkiraan Sendiri)
      </template>

      <template #content>
        <div v-if="dokumen.detail_h_p_s_dokumen_pengadaan && dokumen.detail_h_p_s_dokumen_pengadaan.length > 0">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama Barang/Jasa</th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Satuan</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Harga Satuan</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(hps, index) in dokumen.detail_h_p_s_dokumen_pengadaan" :key="hps.id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ index + 1 }}</td>
                  <td class="px-6 py-4 text-sm text-gray-900">{{ hps.nama_barang_jasa }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{{ hps.volume }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{{ hps.satuan }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{{ formatCurrency(hps.harga_satuan) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">{{ formatCurrency(hps.volume * hps.harga_satuan) }}</td>
                </tr>
                <tr class="bg-gray-50 font-semibold">
                  <td colspan="5" class="px-6 py-4 text-right text-sm text-gray-900">Total HPS:</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {{ formatCurrency(getTotalHPS()) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="text-center py-8 text-gray-500">
          Data HPS tidak tersedia
        </div>
      </template>

      <template #footer>
        <button
          @click="closeHPSModal"
          class="inline-flex items-center px-4 py-2 bg-gray-300 border border-transparent rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-400 focus:outline-none focus:border-gray-400 focus:ring focus:ring-gray-200 active:bg-gray-300 disabled:opacity-25 transition"
        >
          Tutup
        </button>
      </template>
    </dialog-modal>

    <!-- Detail Penilaian Modal -->
    <dialog-modal :show="penilaianModalOpen" @close="closePenilaianModal" :max-width="'7xl'">
      <template #title>
        Detail Penilaian - {{ selectedPengajuan?.penyedia?.nama_perusahaan }}
      </template>

      <template #content>
        <div v-if="selectedPengajuan" class="space-y-6">
          <!-- Perbandingan Harga -->
          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-4">Perbandingan Harga</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="overflow-x-auto">
                <table class="min-w-full">
                  <thead>
                    <tr>
                      <th class="text-left text-sm font-medium text-gray-700 pb-3">Merk/Tipe/Specs</th>
                      <th class="text-left text-sm font-medium text-gray-700 pb-3">Nama Barang/Jasa</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Volume</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Harga Satuan</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Total Penawaran</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Total HPS</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Selisih</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Perbandingan</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(hps, index) in dokumen.detail_h_p_s_dokumen_pengadaan" :key="hps.id"
                        class="border-b border-gray-200">
                      <td class="py-2 text-sm text-gray-600">{{
                        selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index] ?
                        [
                          selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].merk,
                          selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].tipe,
                          selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].spesifikasi
                        ].filter(Boolean).join(' - ') || selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].uraian
                        : '-'
                      }}</td>
                      <td class="py-2 text-sm text-gray-600">{{ hps.nama_barang_jasa }}</td>
                      <td class="py-2 text-right text-sm font-medium">
                        {{ selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]?.volume || '-' }}
                      </td>
                      <td class="py-2 text-right text-sm font-medium">
                        {{ selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]
                          ? formatCurrency(selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan)
                          : '-' }}
                      </td>
                      <td class="py-2 text-right text-sm font-medium">
                        {{ selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]
                          ? formatCurrency(selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                                               selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan)
                          : '-' }}
                      </td>
                      <td class="py-2 text-right text-sm font-medium">{{ formatCurrency(hps.volume * hps.harga_satuan) }}</td>
                      <td class="py-2 text-right text-sm font-medium"
                          :class="selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index] &&
                                 (selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                                  selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan) >
                                 (hps.volume * hps.harga_satuan)
                                 ? 'text-red-600' : 'text-green-600'">
                        {{ selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]
                          ? formatCurrency((selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                                                selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan) -
                                               (hps.volume * hps.harga_satuan))
                          : '-' }}
                      </td>
                      <td class="py-2 text-right text-sm font-medium">
                        <div v-if="selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]"
                             class="flex items-center justify-end space-x-1"
                             :class="getPercentageDiff(
                               selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                               selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan,
                               hps.volume * hps.harga_satuan
                             ) > 0 ? 'text-red-600' : 'text-green-600'">
                          <span class="font-medium">
                            {{ Math.abs(getPercentageDiff(
                              selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                              selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan,
                              hps.volume * hps.harga_satuan
                            )).toFixed(2) }}%
                          </span>
                          <svg v-if="getPercentageDiff(
                            selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                            selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan,
                            hps.volume * hps.harga_satuan
                          ) > 0"
                            class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                          </svg>
                          <svg v-else
                            class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                        <span v-else>-</span>
                      </td>
                    </tr>
                    <!-- Total Row -->
                    <tr class="border-t-2 border-gray-300 font-semibold">
                      <td class="py-3 text-sm text-gray-700">Total</td>
                      <td class="py-3 text-right text-sm">-</td>
                      <td class="py-3 text-right text-sm">-</td>
                      <td class="py-3 text-right text-sm">-</td>
                      <td class="py-3 text-right text-sm">
                        {{ formatCurrency(getTotalNilaiPenawaran(selectedPengajuan)) }}
                      </td>
                      <td class="py-3 text-right text-sm">
                        {{ formatCurrency(getTotalHPS()) }}
                      </td>
                      <td class="py-3 text-right text-sm"
                          :class="getTotalSelisih(selectedPengajuan) > 0 ? 'text-red-600' : 'text-green-600'">
                        {{ formatCurrency(getTotalSelisih(selectedPengajuan)) }}
                      </td>
                      <td class="py-3 text-right text-sm">
                        <div class="flex items-center justify-end space-x-1"
                             :class="getTotalPercentageDiff(selectedPengajuan) > 0 ? 'text-red-600' : 'text-green-600'">
                          <span class="font-medium">{{ Math.abs(getTotalPercentageDiff(selectedPengajuan)).toFixed(2) }}%</span>
                          <svg v-if="getTotalPercentageDiff(selectedPengajuan) > 0"
                            class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                          </svg>
                          <svg v-else
                            class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Penilaian -->
          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-4">Hasil Penilaian</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div v-if="selectedPengajuan.penilaian_penyedia_dokumen_pengadaan && selectedPengajuan.penilaian_penyedia_dokumen_pengadaan.length > 0">
                <div class="space-y-4">
                  <div v-for="penilaian in selectedPengajuan.penilaian_penyedia_dokumen_pengadaan" :key="penilaian.id"
                       class="border-b border-gray-200 pb-4">
                    <div class="flex items-center justify-between mb-2">
                      <label class="block text-sm font-medium text-gray-700">
                        {{ penilaian.bobot_penilaian?.jenis_bobot_penilaian?.nama_jenis_bobot || 
                           penilaian.bobot_penilaian?.kode_bobot?.replace(/_/g, ' ').replace(/\w\S*/g, function(txt) {
                             return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                           }) || 'Kriteria Penilaian' }}
                        ({{ penilaian.bobot_penilaian?.persentase_bobot || 0 }}%)
                      </label>
                      <div class="flex items-center space-x-4">
                        <div class="flex items-center space-x-2">
                          <span class="text-sm font-medium">{{ penilaian.nilai }}</span>
                          <span class="text-sm text-gray-500">/ 100</span>
                        </div>
                        <div class="flex items-center">
                          <input type="checkbox"
                                 :checked="penilaian.is_lulus"
                                 disabled
                                 class="h-4 w-4 text-indigo-600 border-gray-300 rounded opacity-60">
                          <label class="ml-2 block text-sm text-gray-700">
                            {{ penilaian.is_lulus ? 'Lulus' : 'Tidak Lulus' }}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="text-sm text-gray-500">
                      Nilai Akhir: {{ ((penilaian.nilai || 0) * (penilaian.bobot_penilaian?.persentase_bobot || 0) / 100).toFixed(2) }}
                    </div>
                  </div>

                  <!-- Total Score -->
                  <div class="pt-4 border-t-2 border-gray-300">
                    <div class="flex items-center justify-between">
                      <span class="text-lg font-medium text-gray-900">Total Nilai</span>
                      <span class="text-lg font-medium"
                            :class="getTotalNilaiPenilaian(selectedPengajuan) >= 60 ? 'text-green-600' : 'text-red-600'">
                        {{ getTotalNilaiPenilaian(selectedPengajuan).toFixed(2) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-4 text-gray-500">
                Belum ada penilaian untuk pengajuan ini
              </div>
            </div>
          </div>

          <!-- Keterangan -->
          <div v-if="selectedPengajuan.keterangan_penilaian">
            <h3 class="text-lg font-medium text-gray-900 mb-2">Keterangan</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <p class="text-sm text-gray-900">{{ selectedPengajuan.keterangan_penilaian }}</p>
            </div>
          </div>

          <!-- Dokumen -->
          <div v-if="selectedPengajuan.file_kepersertaan_pengadaan && selectedPengajuan.file_kepersertaan_pengadaan.length > 0">
            <h3 class="text-lg font-medium text-gray-900 mb-2">Dokumen</h3>
            <div class="bg-gray-50 p-4 rounded-lg max-w-2xl">
              <div class="space-y-2">
                <div v-for="file in selectedPengajuan.file_kepersertaan_pengadaan" :key="file.id"
                     class="flex items-center p-2 hover:bg-gray-100 rounded-lg group">
                  <div class="flex-grow min-w-0">
                    <span class="text-sm text-gray-900 font-medium block truncate">{{ file.file_kepersertaan_pengadaan_type?.nama || 'Dokumen' }}</span>
                    <span class="text-sm text-gray-500 block truncate">{{ file.nama_file }}</span>
                  </div>
                  <a :href="route('file.download_file_kepersertaan', file.id)"
                     class="ml-4 shrink-0 inline-flex items-center px-2.5 py-1.5 text-sm text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-md"
                     download>
                    <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V3"/>
                    </svg>
                    Download
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          @click="closePenilaianModal"
          class="inline-flex items-center px-4 py-2 bg-gray-300 border border-transparent rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-400 focus:outline-none focus:border-gray-400 focus:ring focus:ring-gray-200 active:bg-gray-300 disabled:opacity-25 transition"
        >
          Tutup
        </button>
      </template>
    </dialog-modal>

    <!-- BAEP Modal -->
    <dialog-modal :show="baepModalOpen" @close="closeBaepModal" :max-width="'7xl'" :closeable="false">
      <template #title>
        Berita Acara Evaluasi Penawaran
      </template>

      <template #content>
        <BeritaAcaraEvaluasiPenawaran
          :dokumen_pengadaan_id="dokumen.id"
        />
      </template>

      <template #footer>
        <div class="flex justify-end space-x-4">
          <button
            @click="closeBaepModal"
            class="inline-flex items-center px-4 py-2 bg-gray-300 border border-transparent rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-400 focus:outline-none focus:border-gray-400 focus:ring focus:ring-gray-200 active:bg-gray-300 disabled:opacity-25 transition"
          >
            Tutup
          </button>
        </div>
      </template>
    </dialog-modal>

    <!-- BAHP Modal -->
    <dialog-modal :show="bahpModalOpen" @close="closeBahpModal" :max-width="'7xl'" :closeable="false">
      <template #title>
        Berita Acara Hasil Penawaran
      </template>

      <template #content>
        <BeritaAcaraHasilPenawaran
          :dokumen_pengadaan_id="dokumen.id"
        />
      </template>

      <template #footer>
        <div class="flex justify-end space-x-4">
          <button
            @click="closeBahpModal"
            class="inline-flex items-center px-4 py-2 bg-gray-300 border border-transparent rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-400 focus:outline-none focus:border-gray-400 focus:ring focus:ring-gray-200 active:bg-gray-300 disabled:opacity-25 transition"
          >
            Tutup
          </button>
        </div>
      </template>
    </dialog-modal>

    <!-- Confirm Setujui Pemenang Modal -->
    <dialog-modal :show="confirmSetujuiModalOpen" @close="closeConfirmSetujuiModal" :max-width="'lg'">
      <template #title>
        <div class="flex items-center">
          <i class="fas fa-check-circle text-green-600 mr-3"></i>
          Konfirmasi Persetujuan Pemenang
        </div>
      </template>

      <template #content>
        <div class="space-y-4">
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex items-start">
              <i class="fas fa-exclamation-triangle text-yellow-600 mt-1 mr-3"></i>
              <div>
                <h4 class="text-sm font-medium text-yellow-800 mb-1">Peringatan</h4>
                <p class="text-sm text-yellow-700">
                  Tindakan ini akan memindahkan dokumen pengadaan ke tahap <strong>pengumuman pemenang</strong> dan tidak dapat dibatalkan.
                </p>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 rounded-lg p-4">
            <h5 class="text-sm font-medium text-gray-900 mb-2">Detail Dokumen:</h5>
            <dl class="space-y-1">
              <div class="flex justify-between text-sm">
                <dt class="text-gray-600">Pemenang:</dt>
                <dd class="text-gray-900 font-medium">
                  {{ pengajuan.find(p => p.is_pemenang)?.penyedia?.nama_perusahaan || '-' }}
                </dd>
              </div>
            </dl>
          </div>

          <p class="text-sm text-gray-600">
            Apakah Anda yakin ingin menyetujui pemenang pengadaan ini?
          </p>

          <!-- Countdown Progress -->
          <div v-if="countdown > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm text-blue-800 font-medium">
                <i class="fas fa-info-circle mr-1"></i>
                Mohon tunggu sebelum melanjutkan
              </span>
              <span class="text-sm text-blue-600 font-bold">{{ countdown }}s</span>
            </div>
            <div class="w-full bg-blue-200 rounded-full h-2">
              <div 
                class="bg-blue-600 h-2 rounded-full transition-all duration-1000 ease-linear"
                :style="{ width: `${(countdown / 5) * 100}%` }"
              ></div>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-3">
          <button
            @click="closeConfirmSetujuiModal"
            :disabled="isSubmittingSetujui"
            class="inline-flex items-center px-4 py-2 bg-gray-300 border border-transparent rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-400 focus:outline-none focus:border-gray-400 focus:ring focus:ring-gray-200 active:bg-gray-300 disabled:opacity-25 transition"
          >
            Batal
          </button>
          <button
            @click="handleConfirmSetujui"
            :disabled="!canConfirmSetujui"
            :class="{
              'bg-green-600 hover:bg-green-700 focus:border-green-700 focus:ring-green-200': canConfirmSetujui,
              'bg-gray-400 cursor-not-allowed': !canConfirmSetujui
            }"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest focus:outline-none focus:ring active:bg-green-600 disabled:opacity-25 transition"
          >
            <i v-if="isSubmittingSetujui" class="fas fa-spinner fa-spin mr-2"></i>
            <i v-else-if="countdown > 0" class="fas fa-clock mr-2"></i>
            <i v-else class="fas fa-check mr-2"></i>
            
            <span v-if="isSubmittingSetujui">Memproses...</span>
            <span v-else-if="countdown > 0">Tunggu {{ countdown }} detik</span>
            <span v-else>Ya, Setujui Pemenang</span>
          </button>
        </div>
      </template>
    </dialog-modal>

    <!-- Confirm Tolak Pemenang Modal -->
    <dialog-modal :show="confirmTolakModalOpen" @close="closeConfirmTolakModal" :max-width="'2xl'">
      <template #title>
        <div class="flex items-center">
          <i class="fas fa-times-circle text-red-600 mr-3"></i>
          Konfirmasi Penolakan Persetujuan Pemenang
        </div>
      </template>

      <template #content>
        <div class="space-y-4">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-start">
              <i class="fas fa-exclamation-triangle text-red-600 mt-1 mr-3"></i>
              <div>
                <h4 class="text-sm font-medium text-red-800 mb-2">Peringatan</h4>
                <div class="text-sm text-red-700 space-y-1">
                  <p>Tindakan ini akan mengakibatkan:</p>
                  <ul class="list-disc list-inside ml-2 space-y-1">
                    <li>Seluruh propose pemenang akan dibatalkan.</li>
                    <li>Pemenang yang telah diset akan dibatalkan.</li>
                    <li>Pengadaan akan kembali ke tahap <strong>"Pembukaan Pengadaan / Penilaian"</strong></li>
                    <li>Pelaksanaan Jadwal <strong>{{ jadwalEvaluasi }}</strong> yang semula selesai tanggal <strong>{{ formatDate(pelaksanaanJadwalWaktuSelesai) }}</strong> akan mundur dan selesai 3 hari dari waktu penolakan</li>
                    <li>Anda harus mengisi alasan penolakan</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label for="alasan_penolakan" class="block text-sm font-medium text-gray-700 mb-2">
              Alasan Penolakan <span class="text-red-600">*</span>
            </label>
            <textarea
              id="alasan_penolakan"
              v-model="alasanPenolakan"
              rows="4"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
              placeholder="Masukkan alasan penolakan persetujuan pemenang..."
            ></textarea>
            <p v-if="alasanPenolakanError" class="mt-1 text-sm text-red-600">{{ alasanPenolakanError }}</p>
          </div>

          <p class="text-sm text-gray-600">
            Apakah Anda yakin ingin menolak persetujuan pemenang pengadaan ini?
          </p>

          <!-- Countdown Progress -->
          <div v-if="countdownTolak > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm text-blue-800 font-medium">
                <i class="fas fa-info-circle mr-1"></i>
                Mohon tunggu sebelum melanjutkan
              </span>
              <span class="text-sm text-blue-600 font-bold">{{ countdownTolak }}s</span>
            </div>
            <div class="w-full bg-blue-200 rounded-full h-2">
              <div 
                class="bg-blue-600 h-2 rounded-full transition-all duration-1000 ease-linear"
                :style="{ width: `${(countdownTolak / 5) * 100}%` }"
              ></div>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-3">
          <button
            @click="closeConfirmTolakModal"
            :disabled="isSubmittingTolak"
            class="inline-flex items-center px-4 py-2 bg-gray-300 border border-transparent rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-400 focus:outline-none focus:border-gray-400 focus:ring focus:ring-gray-200 active:bg-gray-300 disabled:opacity-25 transition"
          >
            Batal
          </button>
          <button
            @click="handleConfirmTolak"
            :disabled="!canConfirmTolak"
            :class="{
              'bg-red-600 hover:bg-red-700 focus:border-red-700 focus:ring-red-200': canConfirmTolak,
              'bg-gray-400 cursor-not-allowed': !canConfirmTolak
            }"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest focus:outline-none focus:ring active:bg-red-600 disabled:opacity-25 transition"
          >
            <i v-if="isSubmittingTolak" class="fas fa-spinner fa-spin mr-2"></i>
            <i v-else-if="countdownTolak > 0" class="fas fa-clock mr-2"></i>
            <i v-else class="fas fa-times mr-2"></i>
            
            <span v-if="isSubmittingTolak">Memproses...</span>
            <span v-else-if="countdownTolak > 0">Tunggu {{ countdownTolak }} detik</span>
            <span v-else>Ya, Tolak Persetujuan Pemenang</span>
          </button>
        </div>
      </template>
    </dialog-modal>

    <Toast position="top-right" />
  </app-layout>
</template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import DialogModal from '@/Jetstream/DialogModal.vue'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import BeritaAcaraEvaluasiPenawaran from '@/Components/DokumenPengadaan/BeritaAcaraEvaluasiPenawaran.vue'
import BeritaAcaraHasilPenawaran from '@/Components/DokumenPengadaan/BeritaAcaraHasilPenawaran.vue'

export default defineComponent({
  components: {
    AppLayout,
    DialogModal,
    Button,
    Toast,
    BeritaAcaraEvaluasiPenawaran,
    BeritaAcaraHasilPenawaran,
  },

  props: {
    dokumen: {
      type: Object,
      required: true,
    },
    pengajuan: {
      type: Array,
      required: true,
    },
  },

  setup() {
    const toast = useToast();
    return { toast }
  },

  data() {
    return {
      hpsModalOpen: false,
      penilaianModalOpen: false,
      selectedPengajuan: null,
      baepModalOpen: false,
      bahpModalOpen: false,
      isSubmittingSetujui: false,
      confirmSetujuiModalOpen: false,
      countdown: 5,
      countdownInterval: null,
      isSubmittingTolak: false,
      confirmTolakModalOpen: false,
      countdownTolak: 5,
      countdownTolakInterval: null,
      alasanPenolakan: '',
      alasanPenolakanError: '',
    }
  },

  computed: {
    canSetujuiPemenang() {
      // Check if user is PPK and document is in verifikasi_pemenang_1f stage
      const userRole = this.$page.props.session_role.role?.name || '';
      const hasWinner = this.pengajuan.some(p => p.is_pemenang);
      
      return (
        userRole === 'ppk' &&
        this.dokumen.kode_tahap === 'verifikasi_pemenang_1f' &&
        hasWinner
      );
    },

    canTolakPemenang() {
      // Check if user is PPK and document is in verifikasi_pemenang_1f stage
      const userRole = this.$page.props.session_role.role?.name || '';
      
      return (
        userRole === 'ppk' &&
        this.dokumen.kode_tahap === 'verifikasi_pemenang_1f'
      );
    },

    canConfirmSetujui() {
      return this.countdown <= 0 && !this.isSubmittingSetujui;
    },

    canConfirmTolak() {
      return this.countdownTolak <= 0 && !this.isSubmittingTolak && this.alasanPenolakan.trim().length > 0;
    },

    jadwalEvaluasi() {
      const evalKode = 'evaluasi_administrasi_kualifikasi_teknis_dan_harga';
      const pelaksanaanJadwal = this.dokumen.pelaksanaan_jadwal?.find(
        pj => pj.kode_jadwal === evalKode
      );
      return pelaksanaanJadwal?.jadwal?.nama_jadwal || 'Evaluasi Administrasi, Kualifikasi, Teknis dan Harga';
    },

    pelaksanaanJadwalWaktuSelesai() {
      const evalKode = 'evaluasi_administrasi_kualifikasi_teknis_dan_harga';
      const pelaksanaanJadwal = this.dokumen.pelaksanaan_jadwal?.find(
        pj => pj.kode_jadwal === evalKode
      );
      return pelaksanaanJadwal?.waktu_selesai || null;
    }
  },

  beforeUnmount() {
    this.resetCountdown();
    this.resetCountdownTolak();
  },

  methods: {
    goBack() {
      this.$inertia.visit(route('dokumen_pengadaan.diajukan_menang'))
    },

    formatDate(dateString) {
      if (!dateString) return null;
      return new Date(dateString).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return 'Rp 0';
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    getTotalNilaiPenawaran(pengajuan) {
      if (!pengajuan.detail_penawaran_kepersertaan_pengadaan) return 0;
      return pengajuan.detail_penawaran_kepersertaan_pengadaan.reduce((sum, item) =>
        sum + (item.volume * item.harga_satuan), 0);
    },

    getTotalHPS() {
      if (!this.dokumen.detail_h_p_s_dokumen_pengadaan) return 0;
      return this.dokumen.detail_h_p_s_dokumen_pengadaan.reduce((sum, item) =>
        sum + (item.volume * item.harga_satuan), 0);
    },

    getTotalSelisih(pengajuan) {
      return this.getTotalNilaiPenawaran(pengajuan) - this.getTotalHPS();
    },

    getTotalPercentageDiff(pengajuan) {
      const totalHPS = this.getTotalHPS();
      if (totalHPS === 0) return 0;
      return ((this.getTotalNilaiPenawaran(pengajuan) - totalHPS) / totalHPS) * 100;
    },

    getPercentageDiff(penawaran, hps) {
      if (hps === 0) return 0;
      return ((penawaran - hps) / hps) * 100;
    },

    getTotalNilaiPenilaian(pengajuan) {
      if (!pengajuan.penilaian_penyedia_dokumen_pengadaan) return 0;
      return pengajuan.penilaian_penyedia_dokumen_pengadaan.reduce((total, penilaian) => {
        const nilai = penilaian.nilai || 0;
        const bobot = penilaian.bobot_penilaian?.persentase_bobot || 0;
        return total + (nilai * bobot / 100);
      }, 0);
    },

    showHPSModal() {
      this.hpsModalOpen = true;
    },

    closeHPSModal() {
      this.hpsModalOpen = false;
    },

    showPenilaianDetail(pengajuan) {
      this.selectedPengajuan = pengajuan;
      this.penilaianModalOpen = true;
    },

    closePenilaianModal() {
      this.penilaianModalOpen = false;
      this.selectedPengajuan = null;
    },

    showBaepModal() {
      this.baepModalOpen = true;
    },

    closeBaepModal() {
      this.baepModalOpen = false;
    },

    showBahpModal() {
      this.bahpModalOpen = true;
    },

    closeBahpModal() {
      this.bahpModalOpen = false;
    },

    confirmSetujuiPemenang() {
      this.confirmSetujuiModalOpen = true;
      this.startCountdown();
    },

    closeConfirmSetujuiModal() {
      this.confirmSetujuiModalOpen = false;
      this.resetCountdown();
    },

    startCountdown() {
      this.countdown = 5;
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.stopCountdown();
        }
      }, 1000);
    },

    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      // Don't reset countdown to 5, keep it at 0 or below
    },

    resetCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      this.countdown = 5;
    },

    handleConfirmSetujui() {
      this.confirmSetujuiModalOpen = false;
      this.setujuiPemenang();
    },

    async setujuiPemenang() {
      this.isSubmittingSetujui = true;
      
      try {
        const response = await axios.post(route('dokumen_pengadaan.setujui_pemenang', this.dokumen.id));
        
        if (response.data.success) {
          this.toast.add({
            severity: 'success',
            summary: 'Sukses',
            detail: response.data.message || 'Berhasil menyetujui pemenang',
            life: 3000
          });

          // Handle redirect
          setTimeout(() => {
            if (response.data.redirect) {
              window.location.href = response.data.redirect;
            } else {
              // Fallback redirect
              window.location.href = 'http://localhost:8000/dokumen_pengadaan/list/diajukan-menang';
            }
          }, 1000);
        } else {
          throw new Error(response.data.message || 'Terjadi kesalahan');
        }
      } catch (error) {
        let errorMessage = 'Terjadi kesalahan';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000
        });
      } finally {
        this.isSubmittingSetujui = false;
      }
    },

    confirmTolakPemenang() {
      this.confirmTolakModalOpen = true;
      this.alasanPenolakan = '';
      this.alasanPenolakanError = '';
      this.startCountdownTolak();
    },

    closeConfirmTolakModal() {
      this.confirmTolakModalOpen = false;
      this.resetCountdownTolak();
      this.alasanPenolakan = '';
      this.alasanPenolakanError = '';
    },

    startCountdownTolak() {
      this.countdownTolak = 5;
      this.countdownTolakInterval = setInterval(() => {
        this.countdownTolak--;
        if (this.countdownTolak <= 0) {
          this.stopCountdownTolak();
        }
      }, 1000);
    },

    stopCountdownTolak() {
      if (this.countdownTolakInterval) {
        clearInterval(this.countdownTolakInterval);
        this.countdownTolakInterval = null;
      }
    },

    resetCountdownTolak() {
      if (this.countdownTolakInterval) {
        clearInterval(this.countdownTolakInterval);
        this.countdownTolakInterval = null;
      }
      this.countdownTolak = 5;
    },

    handleConfirmTolak() {
      // Validate alasan penolakan
      if (!this.alasanPenolakan.trim()) {
        this.alasanPenolakanError = 'Alasan penolakan harus diisi';
        return;
      }

      this.alasanPenolakanError = '';
      this.confirmTolakModalOpen = false;
      this.tolakPemenang();
    },

    async tolakPemenang() {
      this.isSubmittingTolak = true;
      
      try {
        const response = await axios.post(route('dokumen_pengadaan.tolak_pemenang', this.dokumen.id), {
          alasan_penolakan: this.alasanPenolakan
        });
        
        if (response.data.success) {
          this.toast.add({
            severity: 'success',
            summary: 'Sukses',
            detail: response.data.message || 'Berhasil menolak persetujuan pemenang',
            life: 3000
          });

          // Handle redirect
          setTimeout(() => {
            if (response.data.redirect) {
              window.location.href = response.data.redirect;
            } else {
              // Fallback redirect
              window.location.href = 'http://localhost:8000/dokumen_pengadaan/list/diajukan-menang';
            }
          }, 1000);
        } else {
          throw new Error(response.data.message || 'Terjadi kesalahan');
        }
      } catch (error) {
        let errorMessage = 'Terjadi kesalahan';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000
        });
      } finally {
        this.isSubmittingTolak = false;
      }
    }
  }
})
</script>

<style scoped>
/* Simple styles for debugging */
</style> 